import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { keepPrevious } from "@/lib/query/client";
import { qk } from "@/lib/query/keys";
import { AlertTriangle, ChevronRight, FolderGit2, GitBranch, Inbox, Loader2, MessageCircleQuestion, X } from "lucide-react";
import { RunStopButton } from "@/components/run-stop-button";
import { ReviewSection } from "@/components/waiting-view";
import { Badge } from "@/components/ui/badge";
import { Avatar, EmptyState } from "@/components/ui/misc";
import { Page, PageHeader } from "@/components/ui/page";
import { api } from "@/lib/api";
import { useCan } from "@/lib/auth";
import { avatarOf } from "@/lib/avatars";
import { formatDue, relativeTime } from "@/lib/format";
import { kindColor, priorityColor, type ActivityEntry, type AgentWithContext, type ParallelRun, type TaskWithContext, type User } from "@/lib/types";
import { cn } from "@/lib/utils";
import { onlyWaiting } from "@/lib/waiting";
import { useWaiting } from "@/lib/waiting-context";

const selectTasks = (data: { tasks: TaskWithContext[] }) => data.tasks;
const selectRuns = (data: { agents: AgentWithContext[] }) =>
  data.agents.flatMap((agent) => (agent.parallelRuns ?? []).map((run) => ({ ...run, agent })));
const NO_TASKS: TaskWithContext[] = [];
const NO_ACTIVITY: ActivityEntry[] = [];
const NO_RUNS: Array<ParallelRun & { agent: AgentWithContext }> = [];

/**
 * "My tasks": every card assigned to the signed-in user across boards, most urgent
 * first. Agents' own work is read in the Agents view; `claude` is the System actor
 * and holds no queue. An admin also sees the agents' parallel runs under way,
 * because those are the runs working through cards handed to agents.
 */
export function QueueView({
  assignee,
  now,
  onOpenTask,
  users = [],
  showTitle = true,
}: {
  /** The signed-in user's id. */
  assignee: string;
  /** False with the tab layout, whose top bar already names the view. */
  showTitle?: boolean;
  /** For the name of whoever finished a card waiting on review. */
  users?: User[];
  now: number;
  onOpenTask: (taskId: string) => void;
}) {
  const tasksQuery = useQuery({
    queryKey: qk.queue(assignee, { includeDone: false }),
    queryFn: () => api.listTasks({ assignee, includeDone: false }),
    select: selectTasks,
  });
  const tasks = tasksQuery.data ?? NO_TASKS;
  /** A true first load; a refetch behind a cached list shows the list. */
  const loading = tasksQuery.isPending;

  // The same cached list the Agents view and the navigation read — no request of its own.
  const isAdmin = useCan("admin");
  const agentsQuery = useQuery({ queryKey: qk.agents(), queryFn: api.listAgents, enabled: isAdmin, select: selectRuns });
  const runs = (isAdmin && agentsQuery.data) || NO_RUNS;
  const overdue = tasks.filter((task) => task.overdue).length;

  /** Only the cards waiting on the user's answer — admin only, like the list it reads. */
  const waiting = useWaiting();
  const waitingHere = waiting.enabled ? onlyWaiting(tasks, waiting.byTask) : [];
  const [waitingOnly, setWaitingOnly] = useState(false);
  // Nothing left to filter to: put the whole queue back rather than show an empty one.
  useEffect(() => {
    if (waitingOnly && waitingHere.length === 0 && !loading) setWaitingOnly(false);
  }, [waitingOnly, waitingHere.length, loading]);
  const shown = waitingOnly ? waitingHere : tasks;

  return (
    <Page>
      <PageHeader
        title="My tasks"
        showTitle={showTitle}
        description={`Everything assigned to you, most urgent first${waiting.reviews.length > 0 ? ", then what waits on your review" : ""}.`}
        actions={
          overdue > 0 ? (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle /> {overdue} overdue
            </Badge>
          ) : null
        }
      />

      {waitingHere.length > 0 ? (
        <button
          type="button"
          aria-pressed={waitingOnly}
          onClick={() => setWaitingOnly((current) => !current)}
          className={cn(
            "mb-4 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors",
            waitingOnly ? "bg-waiting text-waiting-foreground" : "bg-waiting/15 text-waiting hover:bg-waiting/25",
          )}
        >
          <MessageCircleQuestion className="size-3.5" />
          Waiting on you <span className="tabular-nums">{waitingHere.length}</span>
          {waitingOnly ? <X className="size-3" /> : null}
        </button>
      ) : null}

      {runs.length > 0 ? (
        <section className="mb-5">
          <h2 className="mb-1.5 text-[12px] font-medium uppercase tracking-wide text-muted-foreground">
            Parallel runs ({runs.length})
          </h2>
          <ul className="space-y-1.5">
            {runs.map((run) => {
              const task = run.taskId ? tasks.find((candidate) => candidate.id === run.taskId) : undefined;
              return (
                <li key={run.turnId} className="flex items-center gap-2 rounded-md border border-border/70 bg-card px-3 py-2">
                  <Loader2 className="size-3.5 shrink-0 animate-spin text-primary" />
                  <button
                    className="min-w-0 flex-1 text-left"
                    disabled={!run.taskId}
                    onClick={() => run.taskId && onOpenTask(run.taskId)}
                  >
                    <p className="truncate text-[13px] font-medium">
                      @{run.agent.handle} · {task?.title ?? run.taskId ?? "a card"}
                    </p>
                    <p className="flex items-center gap-1 truncate text-[11.5px] text-muted-foreground">
                      <GitBranch className="size-3 shrink-0" />
                      <span className="truncate">{run.branch ?? "making the worktree…"}</span>
                      {run.claimedAt ? <span className="shrink-0">· {relativeTime(run.claimedAt, now)}</span> : null}
                    </p>
                  </button>
                  <RunStopButton agentId={run.agent.id} turnId={run.turnId} label={`@${run.agent.handle}'s run`} stopping={run.stopping} />
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {loading ? null : shown.length === 0 ? (
        <EmptyState
          icon={<Inbox />}
          title="Nothing assigned to you"
          hint="Cards assigned to you show up here until they are done."
          className="py-12"
        />
      ) : (
        <ul className="space-y-1.5">
          {shown.map((task) => (
            <li key={task.id}>
              <QueueRow task={task} now={now} waiting={waiting.byTask.has(task.id)} onOpen={() => onOpenTask(task.id)} />
            </li>
          ))}
        </ul>
      )}

      {/* The owner's other queue: cards someone finished, waiting on them to accept. Same list and count as Awaiting's. */}
      {waiting.reviews.length > 0 ? (
        <div className="mt-5">
          <ReviewSection
            reviews={waiting.reviews}
            users={users}
            now={now}
            onOpenTask={onOpenTask}
            title="To review"
            storageKey="automation.mine.reviewsOpen"
          />
        </div>
      ) : null}

      {loading ? null : <RecentlyDone assignee={assignee} now={now} onOpenTask={onOpenTask} />}
    </Page>
  );
}

/** One of the user's cards: priority, title, where it is, and when it is due (or when it was done). */
function QueueRow({ task, now, waiting, onOpen }: { task: TaskWithContext; now: number; waiting: boolean; onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      className={cn(
        "group flex w-full items-center gap-3 rounded-md border border-border/70 bg-card px-3 py-2.5 text-left",
        "transition-all hover:-translate-y-px hover:border-ring/40 hover:shadow-[0_6px_16px_-10px_rgba(0,0,0,0.4)]",
        waiting && "border-waiting/60 shadow-[0_0_0_1px_var(--waiting)] hover:border-waiting",
      )}
    >
      <span
        aria-hidden
        className="h-8 w-[3px] shrink-0 rounded-full"
        style={{ backgroundColor: priorityColor(task.priority) }}
      />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 text-[13px] font-medium">
          <span className="truncate">{task.title}</span>
          {waiting ? (
            <MessageCircleQuestion aria-label="Needs your answer" className="size-3.5 shrink-0 text-waiting" />
          ) : null}
        </p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
          <span className="truncate">{task.boardName}</span>
          <span className="opacity-40">·</span>
          <span style={{ color: kindColor(task.columnKind) }}>{task.columnName}</span>
          {/* This list crosses boards, so which codebase a card is in is
              not implied by anything else on the row. */}
          {task.project ? (
            <>
              <span className="opacity-40">·</span>
              <span
                className="inline-flex max-w-40 items-center gap-1 truncate"
                style={{ color: "var(--kind-review)" }}
                title={task.project.hostPath}
              >
                <FolderGit2 className="size-3 shrink-0" />
                {task.project.name}
              </span>
            </>
          ) : null}
          {task.blockedReason ? (
            <>
              <span className="opacity-40">·</span>
              <span className="truncate" style={{ color: kindColor("blocked") }}>
                {task.blockedReason}
              </span>
            </>
          ) : null}
        </p>
      </div>
      <span
        className={cn(
          "shrink-0 text-[11px] font-medium tabular-nums",
          task.overdue ? "text-destructive" : "text-muted-foreground",
        )}
      >
        {task.completedAt ? `done ${relativeTime(task.completedAt, now)}` : task.overdue ? "overdue" : formatDue(task.dueAt, now)}
      </span>
    </button>
  );
}

const RECENT_DONE = 10;

/**
 * The user's most recently finished cards, behind a toggle under the list —
 * the page lists open cards only, so this is where "what did I just close?" is
 * answered, and fetched only when asked for.
 */
const selectRecentlyDone = (data: { tasks: TaskWithContext[] }) =>
  data.tasks
    .filter((task) => task.columnKind === "done")
    .sort((a, b) => (b.completedAt ?? "").localeCompare(a.completedAt ?? ""))
    .slice(0, RECENT_DONE);

function RecentlyDone({
  assignee,
  now,
  onOpenTask,
}: {
  assignee: string;
  now: number;
  onOpenTask: (taskId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const doneQuery = useQuery({
    queryKey: qk.queue(assignee, { includeDone: true }),
    queryFn: () => api.listTasks({ assignee, includeDone: true }),
    enabled: open,
    select: selectRecentlyDone,
  });
  const done = doneQuery.data ?? (doneQuery.isError ? NO_TASKS : null);

  return (
    <section className="mt-5">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="inline-flex items-center gap-1 text-[12px] font-medium text-muted-foreground hover:text-foreground"
      >
        <ChevronRight className={cn("motion-chevron size-3.5", open && "rotate-90")} />
        Recently done
      </button>
      {open ? (
        done === null ? (
          <Loader2 className="mt-2 size-4 animate-spin text-muted-foreground" />
        ) : done.length === 0 ? (
          <p className="mt-2 text-[12px] text-muted-foreground">Nothing finished yet.</p>
        ) : (
          <ul className="mt-2 space-y-1.5 opacity-80">
            {done.map((task) => (
              <li key={task.id}>
                <QueueRow task={task} now={now} waiting={false} onOpen={() => onOpenTask(task.id)} />
              </li>
            ))}
          </ul>
        )
      ) : null}
    </section>
  );
}

const ACTION_TINT: Record<string, string> = {
  "task.created": "var(--kind-active)",
  "task.moved": "var(--primary)",
  "task.deleted": "var(--destructive)",
  "task.commented": "var(--kind-review)",
  "board.created": "var(--kind-done)",
  "board.deleted": "var(--destructive)",
  "column.added": "var(--kind-review)",
  "column.deleted": "var(--destructive)",
  // A foreign agent's link to its machine.
  "agent.link_paired": "var(--kind-done)",
  "agent.link_connected": "var(--kind-done)",
  "agent.link_disconnected": "var(--muted-foreground)",
  "agent.link_revoked": "var(--destructive)",
  "agent.link_code_issued": "var(--kind-review)",
  "agent.link_scope_changed": "var(--kind-review)",
};

/** Merged audit trail across boards, showing whether a change came from the UI or from Claude. */
export function ActivityView({
  boardIds,
  users = [],
  selfId = "me",
  showTitle = true,
}: {
  boardIds: string[];
  /** False with the tab layout, whose top bar already names the view. */
  showTitle?: boolean;
  /** Names for actors other than System and the viewer — members and agents. */
  users?: User[];
  selfId?: string;
}) {
  // One cached entry for the merged trail; the previous one stays up while a new board set loads.
  const query = useQuery({
    queryKey: qk.activity({ boardIds }),
    queryFn: async (): Promise<ActivityEntry[]> => {
      const results = await Promise.all(boardIds.map((boardId) => api.boardActivity(boardId, 40).catch(() => ({ activity: [] }))));
      return results
        .flatMap((result) => result.activity)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 120);
    },
    placeholderData: keepPrevious,
  });
  const entries = query.data ?? NO_ACTIVITY;

  return (
    <Page>
      <PageHeader
        title="Activity"
        showTitle={showTitle}
        description="Every change, and whether it came from this UI, an agent over MCP, or the system."
      />

      {query.isPending ? null : entries.length === 0 ? (
        <EmptyState title="Nothing has happened yet" className="py-12" />
      ) : (
        <ol className="relative space-y-0 border-l border-border/70 pl-4">
          {entries.map((entry) => {
            const tint = ACTION_TINT[entry.action] ?? "var(--muted-foreground)";
            const title = (entry.detail?.title ?? entry.detail?.name ?? null) as string | null;
            return (
              <li key={`${entry.id}-${entry.createdAt}`} className="relative py-1.5">
                <span
                  aria-hidden
                  className="absolute -left-[21px] top-3 size-2 rounded-full ring-2 ring-background"
                  style={{ backgroundColor: tint }}
                />
                <div className="flex flex-wrap items-baseline gap-x-2 text-[12.5px]">
                  {(() => {
                    const actor = users.find((user) => user.id === entry.actorId);
                    const name =
                      entry.actorId === "claude" ? "System" : entry.actorId === selfId ? "You" : (actor?.displayName ?? entry.actorId);
                    // An agent reads by its icon, as it does everywhere else; a person by name.
                    return actor?.kind === "agent" ? (
                      <span className="inline-flex items-center gap-1.5 self-center font-medium">
                        <Avatar {...avatarOf(actor)} size="sm" />
                        {name}
                      </span>
                    ) : (
                      <span className="font-medium">{name}</span>
                    );
                  })()}
                  <span className="font-mono text-[11px]" style={{ color: tint }}>
                    {entry.action}
                  </span>
                  {title ? <span className="truncate text-muted-foreground">{title}</span> : null}
                  {entry.action.startsWith("agent.link_") && typeof entry.detail?.machine === "string" ? (
                    <span className="truncate text-muted-foreground">on {entry.detail.machine}</span>
                  ) : null}
                  {entry.detail?.from && entry.detail?.to ? (
                    <span className="text-muted-foreground">
                      {String(entry.detail.from)} → {String(entry.detail.to)}
                    </span>
                  ) : null}
                  <Badge variant="outline" className="ml-auto shrink-0 py-0 text-[9.5px] uppercase">
                    {entry.source}
                  </Badge>
                  <span className="shrink-0 text-[10.5px] text-muted-foreground">{relativeTime(entry.createdAt)}</span>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Page>
  );
}
